/** Lifts: one lift across the team, each athlete's heaviest set and their best at each rep count. */
import { addDays, mondayOf } from "./days.js";
import { byName, plural } from "./plan.js";
import { LB, weightNumber } from "./sets.js";
import { shownUnit, shownWeight } from "../prefs.js";
import { exerciseKey } from "./text.js";
import type { LiftSet } from "./types.js";

export interface TeamLift {
  key: string;
  name: string;
  last: string;
  athletes: Map<string, LiftSet[]>;
}

export interface TeamLifts {
  list: TeamLift[];
  byKey: Map<string, TeamLift>;
}

export function kg(set: LiftSet): number {
  return set.unit === "lb" ? set.weight * LB : set.weight;
}

export function heavier(a: LiftSet, b: LiftSet): boolean {
  const d = kg(a) - kg(b);
  if (Math.abs(d) > 1e-9) return d > 0;
  if (a.reps !== b.reps) return a.reps > b.reps;
  return a.date > b.date;
}

export function liftSetText(set: LiftSet): string {
  return weightNumber(shownWeight(set.weight, set.unit)) + " " + shownUnit(set.unit) + " × " + set.reps;
}

export function buildLifts(sets: LiftSet[]): TeamLifts {
  const byKey = new Map<string, TeamLift>();
  for (const x of sets) {
    const key = x.key || exerciseKey(x.name);
    if (!key) continue;
    let lift = byKey.get(key);
    if (!lift) {
      lift = { key, name: x.name || key, last: x.date, athletes: new Map() };
      byKey.set(key, lift);
    }
    if (x.date >= lift.last) {
      lift.last = x.date;
      if (x.name) lift.name = x.name;
    }
    const list = lift.athletes.get(x.athleteID);
    if (list) list.push(x);
    else lift.athletes.set(x.athleteID, [x]);
  }
  const list = [...byKey.values()].sort((a, b) => (a.last !== b.last ? (a.last > b.last ? -1 : 1) : byName(a, b)));
  return { list, byKey };
}

/** The heaviest set at each rep count that no heavier set beat at more reps: 120 × 1, 110 × 3, 100 × 5. */
export function repBests(sets: LiftSet[]): LiftSet[] {
  const best = new Map<number, LiftSet>();
  for (const x of sets) {
    const current = best.get(x.reps);
    if (!current || heavier(x, current)) best.set(x.reps, x);
  }
  const counts = [...best.keys()].sort((a, b) => a - b);
  return counts
    .filter((r, i) => !counts.slice(i + 1).some((more) => kg(best.get(more)!) >= kg(best.get(r)!) - 1e-9))
    .map((r) => best.get(r)!);
}

export interface Standing {
  heaviest: LiftSet;
  latest: LiftSet;
  bests: LiftSet[];
  recent: { week: string; kg: number }[];
  change: number | null;
  since: string | null;
}

export function standing(sets: LiftSet[], today: string): Standing {
  let heaviest: LiftSet | null = null;
  let latest: LiftSet | null = null;
  const weeks = new Map<string, LiftSet>();
  for (const x of sets) {
    if (!heaviest || heavier(x, heaviest)) heaviest = x;
    if (!latest || x.date > latest.date || (x.date === latest.date && heavier(x, latest))) latest = x;
    const w = mondayOf(x.date);
    const current = weeks.get(w);
    if (!current || heavier(x, current)) weeks.set(w, x);
  }
  const series = [...weeks.keys()].sort().map((week) => ({ week, kg: kg(weeks.get(week)!) }));
  const from = addDays(mondayOf(today), -49);
  const recent = series.filter((pt) => pt.week >= from);
  const change = recent.length > 1 ? recent[recent.length - 1]!.kg - recent[0]!.kg : null;
  return { heaviest: heaviest!, latest: latest!, bests: repBests(sets), recent, change, since: recent[0]?.week ?? null };
}

export function signed(value: number): string {
  return (value > 0 ? "+" : value < 0 ? "−" : "±") + weightNumber(Math.abs(value));
}

export function changeText(st: Standing): string | null {
  if (st.change == null) return null;
  const unit = shownUnit(st.latest.unit);
  const factor = unit === "lb" ? LB : 1;
  return signed(Math.round((st.change / factor) * 100) / 100) + " " + unit;
}

export function sparkPoints(points: { kg: number }[], w = 72, h = 24): string | null {
  if (points.length < 2) return null;
  const values = points.map((pt) => pt.kg);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  return values
    .map((v, i) => (2 + (i * (w - 4)) / (values.length - 1)).toFixed(1) + "," + (h - 3 - ((v - min) / span) * (h - 6)).toFixed(1))
    .join(" ");
}

export function topOf(lift: TeamLift): LiftSet | null {
  let top: LiftSet | null = null;
  for (const sets of lift.athletes.values()) for (const x of sets) if (!top || heavier(x, top)) top = x;
  return top;
}

export function athletesCount(lift: TeamLift): string {
  return plural(lift.athletes.size, "athlete", "athletes");
}
